/** @format — POST /v1/agents/stream/ SSE client */

import { API_BASE_URL } from "../../config";
import { getToken } from "../../utils/tokenStorage";
import {
  parseStateDeltaOps,
  paymentEventFromSnapshot,
} from "./parseStateDelta";
import { parseToolCallResultContent } from "./parseToolCallResult";
import { normalizeAguiComponents } from "./normalizeComponents";

const STREAM_PATH = "/v1/agents/stream/";
const STREAM_STATE_PATH = "/v1/agents/stream/state";
const TRACKING_STREAM_PATH = "/v1/agents/tracking/stream/";
const TRACKING_STREAM_STATE_PATH = "/v1/agents/tracking/stream/state";

function authHeaders() {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) {
    headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
  }
  return headers;
}

export function createAguiIds() {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `thread_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const runId = `run_${Math.random().toString(36).slice(2, 10)}`;
  const messageId = `msg_${Math.random().toString(36).slice(2, 12)}`;
  return { threadId: uuid, runId, messageId };
}

export function buildStreamPayload({
  threadId,
  runId,
  messages = [],
  state = {},
  forwardedProps = {},
  tools = [],
  context = [],
}) {
  return {
    thread_id: threadId,
    run_id: runId,
    messages,
    state,
    tools,
    context,
    forwarded_props: forwardedProps ?? {},
  };
}

export function buildUserStreamMessage(content, userId, messageId) {
  return {
    id: messageId,
    role: "user",
    content,
    user: { id: userId || "anonymous" },
  };
}

export function buildStreamState({
  sessionId,
  chatId,
  newSession,
  timeZone,
  offsetMinutes,
  shipmentChatScope = false,
  currentShipmentId = null,
}) {
  const state = {
    session_id: sessionId,
    chat_id: chatId,
    new_session: Boolean(newSession),
    time_zone: timeZone,
    offset_minutes: offsetMinutes,
  };

  if (shipmentChatScope && currentShipmentId) {
    state.shipment_chat_scope = true;
    state.current_shipment_id = currentShipmentId;
  }

  return state;
}

async function readSseStream(response, onEvent) {
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(
      errText || `AG-UI stream failed (${response.status})`,
    );
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body for AG-UI stream");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n");
    buffer = parts.pop() ?? "";

    for (const line of parts) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const jsonStr = trimmed.slice(5).trim();
      if (!jsonStr || jsonStr === "[DONE]") continue;
      try {
        const event = JSON.parse(jsonStr);
        onEvent(event);
      } catch {
        /* skip malformed chunks */
      }
    }
  }
}

/**
 * @param {object} payload — full AG-UI request body
 * @param {object} handlers
 * @param {AbortSignal} [signal]
 */
export async function streamAguiRun(payload, handlers = {}, signal, options = {}) {
  const url = `${API_BASE_URL}${STREAM_PATH}`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });

  const ignoreSessionSnapshotUi = options.ignoreSessionSnapshotUi ?? true;

  let text = "";
  let uiComponents = [];
  let threadId = payload.thread_id;
  let runId = payload.run_id;
  let streamError = null;
  let activeTool = null;
  let paymentEvent = null;

  await readSseStream(response, (event) => {
    const type = event?.type;
    handlers.onRawEvent?.(event);

    switch (type) {
      case "RUN_STARTED":
        threadId = event.threadId ?? threadId;
        runId = event.runId ?? runId;
        text = "";
        uiComponents = [];
        handlers.onUiComponents?.([]);
        handlers.onRunStarted?.(event);
        break;

      case "STATE_SNAPSHOT": {
        const snap = event.snapshot ?? event.state ?? {};
        if (Array.isArray(snap.ui_components) && !ignoreSessionSnapshotUi) {
          uiComponents = normalizeAguiComponents(snap.ui_components);
          handlers.onUiComponents?.(uiComponents, { source: "snapshot" });
        }
        const snapPayment = paymentEventFromSnapshot(snap);
        if (snapPayment) {
          paymentEvent = snapPayment;
          handlers.onPaymentEvent?.(snapPayment);
        }
        handlers.onStateSnapshot?.(snap);
        break;
      }

      case "TEXT_MESSAGE_START":
        handlers.onTextStart?.(event);
        break;

      case "TEXT_MESSAGE_CONTENT":
        if (typeof event.delta === "string") {
          text += event.delta;
          handlers.onTextDelta?.(text, event.delta);
        }
        break;

      case "TEXT_MESSAGE_END":
        handlers.onTextEnd?.(text);
        break;

      case "STATE_DELTA":
        if (Array.isArray(event.delta)) {
          const deltaBase = ignoreSessionSnapshotUi ? [] : uiComponents;
          const parsed = parseStateDeltaOps(event.delta, deltaBase);
          uiComponents = parsed.uiComponents;
          if (parsed.paymentEvent) {
            paymentEvent = parsed.paymentEvent;
            handlers.onPaymentEvent?.(parsed.paymentEvent);
          }
          handlers.onUiComponents?.(uiComponents, { source: "delta" });
        }
        break;

      case "TOOL_CALL_START":
        activeTool = event.toolCallName ?? "working";
        handlers.onToolStart?.(event);
        break;

      case "TOOL_CALL_END":
        activeTool = null;
        handlers.onToolEnd?.(event);
        break;

      case "TOOL_CALL_RESULT": {
        const parsed = parseToolCallResultContent(event.content);
        if (parsed?.paymentEvent) {
          paymentEvent = parsed.paymentEvent;
          handlers.onPaymentEvent?.(parsed.paymentEvent);
        }
        handlers.onToolResult?.(event, parsed);
        break;
      }

      case "RUN_FINISHED":
        handlers.onRunFinished?.({
          threadId,
          runId,
          text,
          uiComponents,
          paymentEvent,
        });
        break;

      case "RUN_ERROR":
        streamError = [event.code, event.message ?? event.error ?? "AG-UI run failed"]
          .filter(Boolean)
          .join(": ");
        handlers.onRunError?.(event);
        break;

      default:
        break;
    }
  });

  if (streamError) {
    throw new Error(streamError);
  }

  return { threadId, runId, text, uiComponents, paymentEvent };
}

/** Rehydrate public AG-UI state for a thread */
export async function fetchAguiStreamState(threadId) {
  const url = `${API_BASE_URL}${STREAM_STATE_PATH}`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ thread_id: threadId, session_id: threadId }),
  });

  if (!response.ok) {
    throw new Error(`AG-UI state fetch failed (${response.status})`);
  }

  return response.json();
}

/**
 * Tracking-pinned chat stream (same RunAgentInput as /v1/agents/stream/).
 */
export async function streamTrackingAguiRun(payload, handlers = {}, signal, options = {}) {
  const url = `${API_BASE_URL}${TRACKING_STREAM_PATH}`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });

  const ignoreSessionSnapshotUi = options.ignoreSessionSnapshotUi ?? true;

  let text = "";
  let uiComponents = [];
  let threadId = payload.thread_id;
  let runId = payload.run_id;
  let streamError = null;

  await readSseStream(response, (event) => {
    const type = event?.type;
    handlers.onRawEvent?.(event);

    switch (type) {
      case "RUN_STARTED":
        threadId = event.threadId ?? threadId;
        runId = event.runId ?? runId;
        text = "";
        uiComponents = [];
        handlers.onUiComponents?.([]);
        handlers.onRunStarted?.(event);
        break;

      case "STATE_SNAPSHOT": {
        const snap = event.snapshot ?? event.state ?? {};
        if (Array.isArray(snap.ui_components) && !ignoreSessionSnapshotUi) {
          uiComponents = normalizeAguiComponents(snap.ui_components);
          handlers.onUiComponents?.(uiComponents, { source: "snapshot" });
        }
        handlers.onStateSnapshot?.(snap);
        break;
      }

      case "TEXT_MESSAGE_START":
        handlers.onTextStart?.(event);
        break;

      case "TEXT_MESSAGE_CONTENT":
        if (typeof event.delta === "string") {
          text += event.delta;
          handlers.onTextDelta?.(text, event.delta);
        }
        break;

      case "TEXT_MESSAGE_END":
        handlers.onTextEnd?.(text);
        break;

      case "STATE_DELTA":
        if (Array.isArray(event.delta)) {
          const deltaBase = ignoreSessionSnapshotUi ? [] : uiComponents;
          const parsed = parseStateDeltaOps(event.delta, deltaBase);
          uiComponents = parsed.uiComponents;
          handlers.onUiComponents?.(uiComponents, { source: "delta" });
        }
        break;

      case "RUN_FINISHED":
        handlers.onRunFinished?.({ threadId, runId, text, uiComponents });
        break;

      case "RUN_ERROR":
        streamError = [event.code, event.message ?? event.error ?? "Tracking chat stream failed"]
          .filter(Boolean)
          .join(": ");
        handlers.onRunError?.(event);
        break;

      default:
        break;
    }
  });

  if (streamError) {
    throw new Error(streamError);
  }

  return { threadId, runId, text, uiComponents };
}

/** Rehydrate tracking thread AG-UI state */
export async function fetchTrackingAguiStreamState(threadId) {
  const url = `${API_BASE_URL}${TRACKING_STREAM_STATE_PATH}`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ thread_id: threadId, session_id: threadId }),
  });

  if (!response.ok) {
    throw new Error(`Tracking AG-UI state fetch failed (${response.status})`);
  }

  return response.json();
}
